<template>
  <div class="admin-layout">
    <MenuBar />
    <div class="admin-panel">
      <h1>Admin Panel - ACL Management</h1>
    
    <!-- Feature Management Section (super users only) -->
    <div class="section feature-management" v-if="showFeaturesSection && currentUser && currentUser.isSuperUser">
      <h2>Feature Management</h2>
      
      <div class="feature-stats" v-if="featureStats">
        <div class="stat-card" v-for="(stat, feature) in featureStats.stats" :key="feature">
          <h3>{{ formatFeatureName(feature) }}</h3>
          <div class="stat-details">
            <p><strong>Enabled:</strong> {{ stat.enabled }} ({{ stat.percentage }}%)</p>
            <p><strong>Disabled:</strong> {{ stat.disabled }}</p>
          </div>
          <div class="actions">
            <button 
              @click="enableForAll(feature)" 
              class="btn btn-success"
              :disabled="loading">
              Enable for All
            </button>
            <button 
              @click="disableForAll(feature)" 
              class="btn btn-danger"
              :disabled="loading">
              Disable for All
            </button>
          </div>
        </div>
      </div>

      <!-- Quick Action: Enable Claude Haiku 4.5 for All -->
      <div class="quick-action">
        <button 
          @click="enableClaudeHaikuForAll" 
          class="btn btn-primary btn-lg"
          :disabled="loading">
          <span v-if="loading">⏳ Processing...</span>
          <span v-else>🚀 Enable Claude Haiku 4.5 for All Clients</span>
        </button>
      </div>
    </div>

    <!-- User Management Section -->
    <div class="section user-management" v-if="showUsersSection && canManageUsers">
      <h2>User Management</h2>

      <form class="create-user-form" @submit.prevent="createUser">
        <h3>Create User</h3>
        <div class="form-row">
          <input
            v-model.trim="createForm.username"
            type="text"
            placeholder="Username"
            required
            :disabled="loading"
          />
          <input
            v-model="createForm.password"
            type="password"
            placeholder="Password"
            required
            :disabled="loading"
          />
          <input
            v-model="createForm.confirmPassword"
            type="password"
            placeholder="Confirm Password"
            required
            :disabled="loading"
          />
          <input
            v-model.trim="createForm.email"
            type="email"
            placeholder="Email"
            required
            :disabled="loading"
          />
          <input
            v-model.trim="createForm.employeeId"
            type="text"
            placeholder="Employee ID"
            required
            :disabled="loading"
          />
          <input
            v-model.trim="createForm.phone"
            type="text"
            placeholder="Phone"
            required
            :disabled="loading"
          />
          <select v-model="createForm.role" :disabled="loading">
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="platforms">Platfroms</option>
            <option value="marketing">Marketing</option>
            <option value="sponsers">Sponsers</option>
            <option value="dcm">DCM</option>
            <option value="creative">Creative</option>
            <option value="social">Social</option>
            <option value="super">Super</option>
          </select>
          <select v-model="createForm.isActive" :disabled="loading">
            <option :value="true">Active</option>
            <option :value="false">Inactive</option>
          </select>
          <button type="submit" class="btn btn-primary" :disabled="loading">
            Create User
          </button>
        </div>

        <div class="password-strength">
          <div class="password-strength-label">Password strength: {{ passwordStrength.label }}</div>
          <div class="password-strength-bar">
            <div
              class="password-strength-value"
              :class="passwordStrength.className"
              :style="{ width: passwordStrength.width }"
            ></div>
          </div>
          <small>Use at least 8 characters with one number and one special character.</small>
        </div>

        <div class="feature-selection" v-if="availableFeatures.length">
          <label
            v-for="feature in availableFeatures"
            :key="`create-${feature.value}`"
            class="feature-option"
          >
            <input
              type="checkbox"
              :value="feature.value"
              v-model="createForm.enabledFeatures"
              :disabled="loading"
            />
            {{ feature.label }}
          </label>
        </div>
      </form>
      
      <div class="user-list" v-if="users">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Role</th>
              <th>Enabled Features</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user._id">
              <td>{{ user.name }}</td>
              <td>{{ user.email }}</td>
              <td>
                <span :class="['status-chip', user.isActive ? 'active' : 'inactive']">
                  {{ user.isActive ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td>
                <select 
                  v-model="user.role" 
                  @change="updateRole(user._id, user.role)"
                  :disabled="loading">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="platforms">Platfroms</option>
                  <option value="marketing">Marketing</option>
                  <option value="sponsers">Sponsers</option>
                  <option value="dcm">DCM</option>
                  <option value="creative">Creative</option>
                  <option value="social">Social</option>
                  <option value="super">Super</option>
                </select>
              </td>
              <td>
                <div class="feature-badges">
                  <span 
                    v-for="feature in user.enabledFeatures" 
                    :key="feature"
                    class="badge">
                    {{ formatFeatureName(feature) }}
                  </span>
                </div>
              </td>
              <td>
                <button 
                  @click="manageUserFeatures(user)" 
                  class="btn btn-sm btn-info"
                  :disabled="loading">
                  Manage Features
                </button>
                <button
                  @click="toggleUserStatus(user)"
                  class="btn btn-sm"
                  :class="user.isActive ? 'btn-warning' : 'btn-success'"
                  :disabled="loading"
                >
                  {{ user.isActive ? 'Deactivate' : 'Activate' }}
                </button>
                <button
                  @click="removeUser(user)"
                  class="btn btn-sm btn-danger"
                  :disabled="loading"
                >
                  Soft Delete
                </button>
                <button
                  @click="openResetPassword(user)"
                  class="btn btn-sm btn-reset-pw"
                  :disabled="loading"
                >
                  Reset Password
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <div v-if="resetPwUser" class="modal" @click.self="closeResetPassword">
      <div class="modal-content">
        <h3>Reset Password – {{ resetPwUser.name }}</h3>
        <div class="reset-pw-form">
          <div class="field-group">
            <label>New Password</label>
            <input
              v-model="resetPwForm.newPassword"
              type="password"
              placeholder="New password"
              :disabled="resetPwLoading"
            />
          </div>
          <div class="field-group">
            <label>Confirm Password</label>
            <input
              v-model="resetPwForm.confirmPassword"
              type="password"
              placeholder="Confirm new password"
              :disabled="resetPwLoading"
            />
          </div>
          <div class="password-strength">
            <div class="password-strength-label">Password strength: {{ resetPwStrength.label }}</div>
            <div class="password-strength-bar">
              <div
                class="password-strength-value"
                :class="resetPwStrength.className"
                :style="{ width: resetPwStrength.width }"
              ></div>
            </div>
            <small>At least 8 characters with one number and one special character.</small>
          </div>
          <div v-if="resetPwError" style="color:#dc3545; margin-top:8px; font-size:13px;">{{ resetPwError }}</div>
        </div>
        <div style="display:flex; gap:10px; margin-top:18px;">
          <button
            class="btn btn-danger"
            :disabled="resetPwLoading"
            @click="submitResetPassword"
          >
            {{ resetPwLoading ? 'Resetting...' : 'Reset Password' }}
          </button>
          <button class="btn btn-secondary" @click="closeResetPassword" :disabled="resetPwLoading">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Feature Management Modal -->
    <div v-if="selectedUser" class="modal" @click.self="closeModal">
      <div class="modal-content">
        <h3>Manage Features for {{ selectedUser.name }}</h3>
        <div class="feature-list">
          <div v-for="feature in availableFeatures" :key="feature.value" class="feature-item">
            <label>
              <input 
                type="checkbox" 
                :checked="selectedUser.enabledFeatures.includes(feature.value)"
                @change="toggleFeature(selectedUser._id, feature.value, $event.target.checked)"
                :disabled="loading">
              {{ feature.label }}
            </label>
          </div>
        </div>
        <button @click="closeModal" class="btn btn-secondary">Close</button>
      </div>
    </div>

    <!-- DCM Performance Stats Section -->
    <div class="section performance-stats" v-if="showPerformanceStats">
      <h2>DCM Team Performance - Direct Uploads</h2>
      
      <div class="stats-controls">
        <input type="date" v-model="statsDateFrom" placeholder="From date" />
        <input type="date" v-model="statsDateTo" placeholder="To date" />
        <button class="btn btn-primary" @click="loadPerformanceStats" :disabled="loadingStats">
          {{ loadingStats ? 'Loading...' : 'Load Stats' }}
        </button>
      </div>

      <div v-if="loadingStats" class="empty-state">Loading performance statistics...</div>
      <div v-else-if="!performanceStats.length" class="empty-state">No performance data available.</div>
      <div v-else class="stats-table-wrap">
        <table class="stats-table">
          <thead>
            <tr>
              <th>DCM User</th>
              <th>Total Uploads</th>
              <th>YouTube Links</th>
              <th>Facebook Links</th>
              <th>First Upload</th>
              <th>Last Upload</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="stat in performanceStats" :key="stat.createdBy">
              <td><strong>{{ stat.createdBy }}</strong></td>
              <td>{{ stat.uploadCount }}</td>
              <td>{{ stat.youtubeCount }}</td>
              <td>{{ stat.facebookCount }}</td>
              <td>{{ formatDate(stat.firstUploadAt) }}</td>
              <td>{{ formatDate(stat.lastUploadAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="performanceStats.length" class="stats-summary">
        <h3>Summary</h3>
        <p><strong>Total DCM Users:</strong> {{ performanceSummary.totalDcmUsers }}</p>
        <p><strong>Total Direct Uploads:</strong> {{ performanceSummary.totalUploads }}</p>
        <p><strong>Total YouTube Links:</strong> {{ performanceSummary.totalYoutubeLinks }}</p>
        <p><strong>Total Facebook Links:</strong> {{ performanceSummary.totalFacebookLinks }}</p>
      </div>
    </div>

    </div>
  </div>
</template>

<script>
import adminService from '../services/adminService';
import MenuBar from '../components/MenuBar.vue';

export default {
  name: 'AdminPanel',
  components: {
    MenuBar
  },
  computed: {
    canManageUsers() {
      if (!this.currentUser) return false;
      return (
        this.currentUser.isSuperUser ||
        this.currentUser.role === 'admin' ||
        (this.currentUser.enabledFeatures || []).includes('users_management')
      );
    },
    showUsersSection() {
      return this.$route.path !== '/admin/features';
    },
    showFeaturesSection() {
      return this.$route.path !== '/admin/users';
    },
    showPerformanceStats() {
      return true; // Always show the performance section to admins
    },
    resetPwStrength() {
      const password = this.resetPwForm.newPassword || '';
      let score = 0;
      if (password.length >= 8) score += 1;
      if (/\d/.test(password)) score += 1;
      if (/[^A-Za-z0-9]/.test(password)) score += 1;
      if (password.length >= 12) score += 1;
      if (!password) return { label: 'Not set', className: 'weak', width: '0%' };
      if (score <= 1) return { label: 'Weak', className: 'weak', width: '25%' };
      if (score === 2) return { label: 'Medium', className: 'medium', width: '55%' };
      if (score === 3) return { label: 'Strong', className: 'strong', width: '80%' };
      return { label: 'Very Strong', className: 'very-strong', width: '100%' };
    },
    passwordStrength() {
      const password = this.createForm.password || '';
      let score = 0;
      if (password.length >= 8) score += 1;
      if (/\d/.test(password)) score += 1;
      if (/[^A-Za-z0-9]/.test(password)) score += 1;
      if (password.length >= 12) score += 1;

      if (!password) {
        return { label: 'Not set', className: 'weak', width: '0%' };
      }
      if (score <= 1) {
        return { label: 'Weak', className: 'weak', width: '25%' };
      }
      if (score === 2) {
        return { label: 'Medium', className: 'medium', width: '55%' };
      }
      if (score === 3) {
        return { label: 'Strong', className: 'strong', width: '80%' };
      }
      return { label: 'Very Strong', className: 'very-strong', width: '100%' };
    }
  },
  data() {
    return {
      currentUser: null,
      users: [],
      availableFeatures: [],
      featureStats: null,
      selectedUser: null,
      resetPwUser: null,
      resetPwForm: { newPassword: '', confirmPassword: '' },
      resetPwLoading: false,
      resetPwError: '',
      loading: false,
      createForm: {
        username: '',
        password: '',
        confirmPassword: '',
        email: '',
        employeeId: '',
        phone: '',
        role: 'user',
        isActive: true,
        enabledFeatures: []
      },
      // Performance stats properties
      performanceStats: [],
      performanceSummary: {
        totalDcmUsers: 0,
        totalUploads: 0,
        totalYoutubeLinks: 0,
        totalFacebookLinks: 0,
      },
      loadingStats: false,
      statsDateFrom: '',
      statsDateTo: '',
    };
  },
  mounted() {
    this.loadCurrentUser();
  },
  methods: {
    async loadCurrentUser() {
      try {
        const api = require('../api').default;
        const res = await api.get('/check');
        if (res.data && res.data.logged_in) {
          this.currentUser = {
            username: res.data.user,
            role: res.data.role,
            isSuperUser: !!res.data.isSuperUser,
            enabledFeatures: res.data.enabledFeatures || [],
          };
        }
      } catch (_) {
        // silently ignore
      }
      this.fetchData();
    },

    async fetchData() {
      try {
        this.loading = true;
        const requests = [adminService.getAllUsers()];
        if (this.currentUser && this.currentUser.isSuperUser) {
          requests.push(adminService.getFeatures(), adminService.getFeatureStats());
        }
        const results = await Promise.all(requests);
        this.users = results[0];
        if (results[1]) this.availableFeatures = results[1].features;
        if (results[2]) this.featureStats = results[2];
      } catch (error) {
        this.showNotification('Failed to load data', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async enableClaudeHaikuForAll() {
      if (!confirm('Are you sure you want to enable Claude Haiku 4.5 for ALL clients?')) {
        return;
      }
      
      try {
        this.loading = true;
        const result = await adminService.enableFeatureForAll('claude_haiku_4_5');
        this.showNotification(
          `Claude Haiku 4.5 enabled for ${result.updatedCount} clients!`, 
          'success'
        );
        await this.fetchData();
      } catch (error) {
        this.showNotification('Failed to enable feature', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async enableForAll(feature) {
      if (!confirm(`Enable ${this.formatFeatureName(feature)} for all clients?`)) {
        return;
      }
      
      try {
        this.loading = true;
        const result = await adminService.enableFeatureForAll(feature);
        this.showNotification(result.message, 'success');
        await this.fetchData();
      } catch (error) {
        this.showNotification('Failed to enable feature', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async disableForAll(feature) {
      if (!confirm(`Disable ${this.formatFeatureName(feature)} for all clients?`)) {
        return;
      }
      
      try {
        this.loading = true;
        const result = await adminService.disableFeatureForAll(feature);
        this.showNotification(result.message, 'success');
        await this.fetchData();
      } catch (error) {
        this.showNotification('Failed to disable feature', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async updateRole(userId, role) {
      try {
        this.loading = true;
        await adminService.updateUserRole(userId, role);
        this.showNotification('Role updated successfully', 'success');
      } catch (error) {
        this.showNotification('Failed to update role', 'error');
        await this.fetchData();
      } finally {
        this.loading = false;
      }
    },

    async createUser() {
      try {
        if (this.createForm.password !== this.createForm.confirmPassword) {
          this.showNotification('Password and confirm password do not match', 'error');
          return;
        }
        if (!this.isStrongPassword(this.createForm.password)) {
          this.showNotification('Password must be at least 8 characters with at least one number and one special character', 'error');
          return;
        }

        this.loading = true;
        await adminService.createUser({
          username: this.createForm.username,
          password: this.createForm.password,
          confirmPassword: this.createForm.confirmPassword,
          email: this.createForm.email,
          employeeId: this.createForm.employeeId,
          phone: this.createForm.phone,
          role: this.createForm.role,
          isActive: this.createForm.isActive,
          enabledFeatures: this.createForm.enabledFeatures
        });
        this.showNotification('User created successfully', 'success');
        this.createForm = {
          username: '',
          password: '',
          confirmPassword: '',
          email: '',
          employeeId: '',
          phone: '',
          role: 'user',
          isActive: true,
          enabledFeatures: []
        };
        await this.fetchData();
      } catch (error) {
        const errorMessage =
          error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : 'Failed to create user';
        this.showNotification(errorMessage, 'error');
      } finally {
        this.loading = false;
      }
    },

    isStrongPassword(password) {
      return password.length >= 8 && /\d/.test(password) && /[^A-Za-z0-9]/.test(password);
    },

    async toggleUserStatus(user) {
      const targetStatus = !user.isActive;
      const actionText = targetStatus ? 'activate' : 'deactivate';
      if (!confirm(`Are you sure you want to ${actionText} ${user.username}?`)) {
        return;
      }

      try {
        this.loading = true;
        await adminService.updateUserStatus(user._id, targetStatus);
        this.showNotification('User status updated', 'success');
        await this.fetchData();
      } catch (error) {
        const errorMessage =
          error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : 'Failed to update user status';
        this.showNotification(errorMessage, 'error');
      } finally {
        this.loading = false;
      }
    },

    async removeUser(user) {
      if (!confirm(`Soft delete user ${user.username}? This cannot be undone from UI.`)) {
        return;
      }

      try {
        this.loading = true;
        await adminService.softDeleteUser(user._id);
        this.showNotification('User soft-deleted successfully', 'success');
        await this.fetchData();
      } catch (error) {
        const errorMessage =
          error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : 'Failed to soft-delete user';
        this.showNotification(errorMessage, 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async toggleFeature(userId, feature, enabled) {
      try {
        this.loading = true;
        if (enabled) {
          await adminService.enableFeatureForUser(userId, feature);
        } else {
          await adminService.disableFeatureForUser(userId, feature);
        }
        this.showNotification('Feature updated successfully', 'success');
        await this.fetchData();
      } catch (error) {
        this.showNotification('Failed to update feature', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    openResetPassword(user) {
      this.resetPwUser = user;
      this.resetPwForm = { newPassword: '', confirmPassword: '' };
      this.resetPwError = '';
    },

    closeResetPassword() {
      this.resetPwUser = null;
      this.resetPwForm = { newPassword: '', confirmPassword: '' };
      this.resetPwError = '';
    },

    async submitResetPassword() {
      this.resetPwError = '';
      const { newPassword, confirmPassword } = this.resetPwForm;
      if (!newPassword) { this.resetPwError = 'New password is required'; return; }
      if (newPassword !== confirmPassword) { this.resetPwError = 'Passwords do not match'; return; }
      if (!this.isStrongPassword(newPassword)) {
        this.resetPwError = 'Password must be at least 8 characters with one number and one special character';
        return;
      }
      try {
        this.resetPwLoading = true;
        await adminService.resetUserPassword(this.resetPwUser._id, newPassword, confirmPassword);
        this.showNotification(`Password for ${this.resetPwUser.name} reset successfully`, 'success');
        this.closeResetPassword();
      } catch (err) {
        this.resetPwError =
          err && err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : 'Failed to reset password';
      } finally {
        this.resetPwLoading = false;
      }
    },

    manageUserFeatures(user) {
      this.selectedUser = user;
    },
    
    closeModal() {
      this.selectedUser = null;
    },
    
    formatFeatureName(feature) {
      return feature
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },
    
    showNotification(message, type = 'info') {
      this.$notify(message, type, 3000);
    },

    async loadPerformanceStats() {
      this.loadingStats = true;
      try {
        const sponsorService = require('../services/sponsorService').default;
        const filters = {};
        if (this.statsDateFrom) filters.dateFrom = this.statsDateFrom;
        if (this.statsDateTo) filters.dateTo = this.statsDateTo;
        
        const response = await sponsorService.getDirectUploadStats(filters);
        this.performanceStats = response.items || [];
        this.performanceSummary = response.summary || {
          totalDcmUsers: 0,
          totalUploads: 0,
          totalYoutubeLinks: 0,
          totalFacebookLinks: 0,
        };
      } catch (error) {
        this.showNotification('Failed to load performance stats', 'error');
      } finally {
        this.loadingStats = false;
      }
    },

    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }
};
</script>

<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  background: #f5f5f5;
}

.admin-panel {
  flex: 1;
  margin-left: 250px;
  overflow-y: auto;
  padding: 20px;
}

h1 {
  color: #333;
  margin-bottom: 30px;
}

.section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

h2 {
  color: #555;
  margin-bottom: 20px;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
}

.feature-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  background: #f9f9f9;
}

.stat-card h3 {
  margin-top: 0;
  color: #333;
  font-size: 18px;
}

.stat-details {
  margin: 15px 0;
}

.stat-details p {
  margin: 5px 0;
}

.actions {
  display: flex;
  gap: 10px;
}

.quick-action {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-lg {
  padding: 15px 30px;
  font-size: 16px;
  font-weight: bold;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-info:hover:not(:disabled) {
  background: #138496;
}

.btn-sm {
  padding: 5px 10px;
  font-size: 12px;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #f8f9fa;
}

th, td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

th {
  font-weight: 600;
  color: #333;
}

select {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.create-user-form {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fafafa;
}

.create-user-form h3 {
  margin: 0 0 10px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  align-items: center;
}

.form-row input,
.form-row select {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
}

.feature-selection {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.password-strength {
  margin-top: 10px;
}

.password-strength-label {
  font-size: 13px;
  color: #444;
}

.password-strength-bar {
  width: 100%;
  max-width: 380px;
  height: 8px;
  border-radius: 999px;
  background: #e9ecef;
  overflow: hidden;
  margin: 6px 0;
}

.password-strength-value {
  height: 100%;
  transition: width 0.2s ease;
}

.password-strength-value.weak {
  background: #dc3545;
}

.password-strength-value.medium {
  background: #ffc107;
}

.password-strength-value.strong {
  background: #28a745;
}

.password-strength-value.very-strong {
  background: #007bff;
}

.feature-option {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.feature-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.badge {
  background: #007bff;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.status-chip {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-chip.active {
  background: #e7f7ec;
  color: #1f7a3f;
}

.status-chip.inactive {
  background: #fbeaec;
  color: #a61f2d;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
}

.btn-warning:hover:not(:disabled) {
  background: #e0a800;
}

.btn-reset-pw {
  background: #6f42c1;
  color: white;
}

.btn-reset-pw:hover:not(:disabled) {
  background: #5a32a3;
}

.reset-pw-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

.reset-pw-form .field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.reset-pw-form label {
  font-size: 13px;
  font-weight: 600;
  color: #444;
}

.reset-pw-form input {
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
}

.modal-content h3 {
  margin-top: 0;
}

.feature-list {
  margin: 20px 0;
}

.feature-item {
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.feature-item label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 20px;
  border-radius: 4px;
  color: white;
  font-weight: 500;
  z-index: 2000;
  animation: slideIn 0.3s;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.notification.success {
  background: #28a745;
}

.notification.error {
  background: #dc3545;
}

.notification.info {
  background: #17a2b8;
}

/* Performance Stats Styles */
.performance-stats {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.stats-controls {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.stats-controls input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.stats-table-wrap {
  overflow-x: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 16px;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
}

.stats-table th,
.stats-table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  text-align: left;
  font-size: 13px;
}

.stats-table th {
  background: #f5f5f5;
  font-weight: 700;
  color: #333;
}

.stats-table td {
  color: #555;
}

.stats-table tr:hover {
  background: #f9f9f9;
}

.stats-summary {
  background: #e8f4f8;
  border: 1px solid #b3d9e6;
  border-radius: 4px;
  padding: 12px 16px;
}

.stats-summary h3 {
  margin-top: 0;
  color: #0a5a7a;
}

.stats-summary p {
  margin: 8px 0;
  color: #2c5aa0;
}
</style>
